/**
 * Client-perceived TTFT (time-to-first-token).
 *
 * The clock is armed the instant a user send fires — each of the three
 * user-send paths (new task, continuation, teammate) — and stopped at
 * the first agent streaming delta rendered for that task. It fires exactly once
 * per user-send (`take` clears the armed state) and never for resumed history
 * (a resume never arms). State lives on the task itself, so parallel sessions
 * never cross-contaminate — no module-level globals.
 *
 * These are pure functions over the task draft; the store/handler glue owns the
 * mutation and the posthog.capture. Kept separate so the timing logic is unit-
 * testable without the socket or store.
 */

import type {
  PerceivedSendPath,
  PerceivedTtftState,
  TaskState,
} from "../types";

/** First streamed content that stops the clock — text or reasoning. */
export type FirstTokenKind = "text" | "thinking";

/** The timing half of the `task:first_token` event; the handler adds where
 *  and on what it ran (surface, runtime, model, input mode). */
export interface PerceivedFirstTokenEvent {
  task_id: string;
  trace_id: string;
  ttft_ms: number;
  send_path: PerceivedSendPath;
  first_token_kind: FirstTokenKind;
}

/**
 * Arm the clock for a user send. Overwrites any prior arm — a fresh send always
 * re-measures from now (a still-armed prior send that never produced a token is
 * simply discarded).
 */
export function armPerceivedTtft(
  task: Pick<TaskState, "perceivedTtft">,
  sendPath: PerceivedSendPath,
  traceId: string,
  now: number = Date.now()
): void {
  const state: PerceivedTtftState = { sentAt: now, sendPath, traceId };
  task.perceivedTtft = state;
}

/**
 * If the task has an armed clock, clear it and return the event to capture;
 * otherwise return null (already fired, or never armed). The clear is what makes
 * it fire exactly once per send.
 */
export function takePerceivedFirstToken(
  task: Pick<TaskState, "taskId" | "perceivedTtft">,
  kind: FirstTokenKind,
  now: number = Date.now()
): PerceivedFirstTokenEvent | null {
  const armed = task.perceivedTtft;
  if (!armed) return null;
  task.perceivedTtft = null;
  return {
    task_id: task.taskId,
    trace_id: armed.traceId,
    ttft_ms: Math.max(0, now - armed.sentAt),
    send_path: armed.sendPath,
    first_token_kind: kind,
  };
}
